/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ButtonProps } from "@eisinc/MobiusOverrides/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Color from "color";
import { css } from "styled-components";

import { xBaseTheme } from "@eisinc/XDL/types";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";

const tertiaryColor = "common.ui1";
const tertiaryInverse = "common.white";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
    };

const buttonSizeVariants = {
    small: {
        height: 32,
        fontSize: 14,
        padding: "0 20px",
        icon: 13,
        iconPadding: 5,
    },
    medium: {
        height: 40,
        fontSize: 16,
        padding: "0 25px",
        icon: 15,
        iconPadding: 5,
    },
    large: {
        height: 40,
        fontSize: 16,
        padding: "0 30px",
        icon: 15,
        iconPadding: 5,
    },
};

const buttonPresentationStyle = (buttonType: string) => {
    return {
        ...ButtonPresentationPropsDefault,
        color: buttonType,
        buttonType: "solid",
        shape: "rectangle",
        sizeVariant: "medium",
        margeCss: true,
        css: css`
            &:hover {
                outline: none;
            }
            &:focus {
                outline: none;
                border-color: ${getColor("secondary")};
            }
            &:disabled {
                border: 0;
                border: 2px solid
                    ${buttonType === "primary"
                        ? getColor("primary")
                        : buttonType === "secondary"
                        ? getColor("common.white")
                        : getColor(tertiaryColor)};
                opacity: 0.5;
                &,
                &:hover {
                    background: ${buttonType === "primary"
                        ? getColor("primary")
                        : buttonType === "secondary"
                        ? getColor("common.white")
                        : getColor(tertiaryColor)};
                }
            }
            height: auto;
            min-height: ${(props: BWT) =>
                props["_size"] || get(buttonSizeVariants, [props["_sizeVariant"], "height"])}px;
            padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};

            > * {
                font-size: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "fontSize"])}px;
                font-weight: normal;
            }
            text-shadow: none;
            font-weight: normal;
            letter-spacing: 0.5px;
            line-height: 18px;
            border-radius: 0px;
            text-transform: uppercase;
        `,
    };
};

export default {
    primary: {
        ...buttonPresentationStyle("primary"),
        css: [
            buttonPresentationStyle("primary").css,
            css`
                &:active {
                    background: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
                &:hover {
                    background: ${({ theme }: { theme: BaseTheme }) =>
                        Color(get(theme, "colors.primary.main")).darken(0.1).rgb().toString()};
                    border-color: ${({ theme }: { theme: BaseTheme }) =>
                        Color(get(theme, "colors.primary.main")).darken(0.1).rgb().toString()};
                }
            `,
        ],
    },
    secondary: {
        ...buttonPresentationStyle("secondary"),
        css: [
            buttonPresentationStyle("secondary").css,
            css`
                &,
                &:active {
                    background: ${getColor("common.white")};
                    border-color: ${getColor("secondary")};
                    color: ${getColor("secondary")};
                }

                &:hover,
                &:focus {
                    outline: none;
                    background: ${getColor("secondary")};
                    color: ${getColor("common.white")};
                    border-color: ${getColor("secondary")};
                }

                &:disabled {
                    border: 2px solid ${getColor(tertiaryColor)};
                    opacity: 0.5;
                    &,
                    &:hover {
                        background: ${getColor("common.white")};
                        border: 2px solid ${getColor("secondary")};
                        color: ${getColor("secondary")};
                    }
                }
            `,
        ],
    },
    tertiary: {
        ...buttonPresentationStyle("tertiary"),
        css: [
            buttonPresentationStyle("tertiary").css,
            css`
                &,
                &:active {
                    background: ${getColor(tertiaryColor)};
                    border: 2px solid ${getColor(tertiaryColor)};
                    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);
                    color: ${getColor(tertiaryInverse)};
                }

                &:focus {
                    border-color: ${getColor("secondary")};
                }
                &:hover {
                    outline: none;
                    background: ${({ theme }: { theme: BaseTheme }) =>
                        Color(get(theme, `colors.${tertiaryColor}`))
                            .darken(0.1)
                            .rgb()
                            .toString()};
                    color: ${getColor(tertiaryInverse)};
                    border-color: ${({ theme }: { theme: BaseTheme }) =>
                        Color(get(theme, `colors.${tertiaryColor}`))
                            .darken(0.1)
                            .rgb()
                            .toString()};
                }
            `,
        ],
    },
} as xBaseTheme["button"];
